import { HelpChatWidget } from '@auctisync/ui';
import { Layout } from '../../components/Layout';
import { PageHead } from '../../components/PageHead';
import { tenantDisplayName, useTenant } from '../../contexts/TenantContext';

/**
 * /ask — Help Center AI assistant (Module #33).
 *
 * Anonymous, no auth required. The IE Worker accepts `/v1/help/ask` without
 * a Bearer token and downgrades the caller to platform-published scope.
 *
 * In white-label mode (tenant context present): the chat headline and the
 * citation links are rewritten to the tenant's brand + help domain.
 */
export function Ask() {
  const tenant = useTenant();
  const baseUrl = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  const askWho = `Ask ${tenantDisplayName(tenant)}`;
  // Module #33 white-label chunk 4: citation links use the tenant's help
  // origin so cited articles open on the tenant's branded surface, not
  // help.auctisync.com.
  const helpBaseUrl = tenant?.help_origin ?? 'https://help.auctisync.com';

  if (!baseUrl) {
    return (
      <Layout>
        <PageHead
          title={askWho}
          description={`AI help assistant for ${tenantDisplayName(tenant)}.`}
        />
        <div className="mx-auto max-w-3xl px-6 py-10">
          <h1 className="mb-3 text-2xl font-bold" style={{ color: 'var(--text)' }}>
            {askWho}
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            The assistant isn't configured on this build.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHead
        title={askWho}
        description="Ask the AI help assistant a question and get an answer grounded in published help articles."
      />
      <div className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="mb-2 text-2xl font-bold" style={{ color: 'var(--text)' }}>
          {askWho}
        </h1>
        <p className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>
          Type a question. The assistant answers only from published help articles and links to its
          sources.
        </p>
        <HelpChatWidget
          baseUrl={baseUrl}
          helpBaseUrl={helpBaseUrl}
          welcomeMessage='Try: "How do I create a bidder segment?" or "What is the refund flow?"'
        />
      </div>
    </Layout>
  );
}
